type InvoiceIssue = {
  message?: string;
  missing_fields?: string[];
};

type InvoiceErrorBody = {
  code?: string;
  message?: string;
  notazz_message?: string;
  enotas_message?: string;
  issues?: InvoiceIssue[];
};

export function formatInvoiceIssues(issues: InvoiceIssue[]): string {
  return issues
    .map((issue) =>
      issue.missing_fields?.length
        ? `• ${issue.message ?? ""}: ${issue.missing_fields.join(", ")}`
        : `• ${issue.message ?? ""}`,
    )
    .join("\n");
}

function normalizeMessage(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

/** Join messages without repeating the same text. */
export function joinUniqueMessages(
  ...parts: Array<string | null | undefined>
): string {
  const result: string[] = [];

  for (const part of parts) {
    if (typeof part !== "string") continue;
    const trimmed = part.trim();
    if (!trimmed) continue;

    const normalized = normalizeMessage(trimmed);
    const alreadyCovered = result.some((existing) => {
      const existingNorm = normalizeMessage(existing);
      return (
        existingNorm === normalized ||
        existingNorm.includes(normalized) ||
        normalized.includes(existingNorm)
      );
    });

    if (alreadyCovered) {
      // Keep the longer / more specific version when one contains the other.
      const idx = result.findIndex((existing) => {
        const existingNorm = normalizeMessage(existing);
        return (
          existingNorm === normalized ||
          existingNorm.includes(normalized) ||
          normalized.includes(existingNorm)
        );
      });
      if (idx >= 0 && trimmed.length > result[idx].length) {
        result[idx] = trimmed;
      }
      continue;
    }

    result.push(trimmed);
  }

  return result.join("\n");
}

export function formatInvoiceEmitError(
  error: unknown,
  messages: { timeout: string; fallback: string },
): string {
  const ax = error as {
    code?: string;
    message?: string;
    response?: { data?: Record<string, unknown> };
  };

  if (ax.code === "ECONNABORTED") {
    return messages.timeout;
  }

  const data = ax.response?.data;
  if (data && typeof data === "object") {
    const top = typeof data.message === "string" ? data.message : "";
    const errObj = data.error;
    if (errObj && typeof errObj === "object" && errObj !== null) {
      const err = errObj as InvoiceErrorBody;
      if (err.code === "VALIDATION_FAILED" && err.issues?.length) {
        return formatInvoiceIssues(err.issues);
      }
      // Prefer provider-specific detail over generic wrappers.
      const specific = [
        err.notazz_message,
        err.enotas_message,
        err.message,
      ].find((message) => typeof message === "string" && message.trim() !== "");

      const combined = joinUniqueMessages(top, specific);
      if (combined) return combined;
    }
    if (top) return top.trim();
  }

  if (
    typeof ax.message === "string" &&
    ax.message &&
    ax.message !== "Network Error"
  ) {
    return ax.message;
  }

  return messages.fallback;
}
